import type { ColumnDef } from "@tanstack/react-table";
import type { TFunction } from "i18next";

import { fmtRelative } from "~/shared/lib/fmt";
import { cn } from "~/shared/lib/utils";
import { Badge } from "~/shared/ui/badge";
import type { Asset } from "../types";
import { AssetThumb } from "../ui/asset-media";

export interface AssetColumnsOptions {
    /** Show the Creator column (workspace owners/admins only — privacy). */
    showCreator: boolean;
    /** Click on the preview thumbnail → open the lightbox (the rest of the row → source chat). */
    onOpenMedia?: (asset: Asset) => void;
}

/**
 * Column model for the /assets library table. Server-side sortable columns are `modelName`
 * and `createdAt` (their ids map to the backend `sort_by` whitelist via asset-filter-utils);
 * the rest are display-only. The `scene` column is shown by default (toggleable),
 * rendering "—" when the asset's current chat has no scene.
 */
export function getAssetColumns(
    t: TFunction,
    { showCreator, onOpenMedia }: AssetColumnsOptions,
): ColumnDef<Asset, unknown>[] {
    return [
        {
            id: "preview",
            header: () => null,
            enableSorting: false,
            enableHiding: false,
            meta: { section: { headClassName: "w-20" } },
            cell: ({ row }) => (
                <div
                    role={onOpenMedia ? "button" : "presentation"}
                    tabIndex={onOpenMedia ? 0 : undefined}
                    onClick={
                        onOpenMedia
                            ? (e) => {
                                  e.stopPropagation();
                                  onOpenMedia(row.original);
                              }
                            : undefined
                    }
                    onKeyDown={
                        onOpenMedia
                            ? (e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      onOpenMedia(row.original);
                                  }
                              }
                            : undefined
                    }
                    className={cn(
                        "h-12 w-16 overflow-hidden rounded bg-muted",
                        onOpenMedia && "cursor-zoom-in",
                    )}
                >
                    <AssetThumb asset={row.original} />
                </div>
            ),
        },
        {
            id: "type",
            header: t("assets:columns.type"),
            enableSorting: false,
            cell: ({ row }) => (
                <Badge variant="secondary" className="font-normal">
                    {t(`assets:type.${row.original.type}`)}
                </Badge>
            ),
        },
        {
            id: "modelName",
            header: t("assets:columns.model"),
            enableSorting: true,
            cell: ({ row }) => (
                <span className="line-clamp-1 max-w-[180px] text-sm text-muted-foreground">
                    {row.original.modelName}
                </span>
            ),
        },
        {
            id: "mode",
            header: t("assets:columns.mode"),
            enableSorting: false,
            meta: { section: { hideAt: "md" } },
            cell: ({ row }) => (
                <Badge variant="outline" className="font-normal">
                    {t(`assets:mode.${row.original.mode}`)}
                </Badge>
            ),
        },
        {
            id: "project",
            header: t("assets:columns.project"),
            enableSorting: false,
            meta: { section: { hideAt: "md" } },
            cell: ({ row }) => (
                <span className="line-clamp-1 max-w-[180px] text-sm text-muted-foreground">
                    {row.original.projectName ?? "—"}
                </span>
            ),
        },
        {
            id: "scene",
            header: t("assets:columns.scene"),
            enableSorting: false,
            meta: { section: { hideAt: "lg" } },
            cell: ({ row }) => (
                <span className="line-clamp-1 max-w-[180px] text-sm text-muted-foreground">
                    {row.original.sceneName ?? "—"}
                </span>
            ),
        },
        ...(showCreator
            ? [
                  {
                      id: "creator",
                      header: t("assets:columns.creator"),
                      enableSorting: false,
                      meta: { section: { hideAt: "lg" } },
                      cell: ({ row }: { row: { original: Asset } }) => (
                          <span className="line-clamp-1 max-w-[180px] text-sm text-muted-foreground">
                              {row.original.creatorName ?? "—"}
                          </span>
                      ),
                  } satisfies ColumnDef<Asset, unknown>,
              ]
            : []),
        {
            id: "createdAt",
            header: t("assets:columns.created"),
            enableSorting: true,
            cell: ({ row }) => (
                <span className="text-sm whitespace-nowrap text-muted-foreground tabular-nums">
                    {fmtRelative(row.original.createdAt)}
                </span>
            ),
        },
    ];
}
